import {defineStore} from "pinia"
import useFileUpload from "@/composables/useFileUpload"

export type UploadStatus = "pending" | "uploading" | "success" | "error"

export type UploadItem = {
  id: string
  name: string
  size: number
  folderId?: number
  progress: number
  indeterminate: boolean
  status: UploadStatus
  message: string | null
}

const DISMISS_DELAY = 5000

const generateId = (): string => {
  return Date.now().toString() + Math.random().toString()
}

export const useUploadStore = defineStore("upload", {
  state: () => ({
    items: [] as UploadItem[],
    completed: {} as Record<number, number>,
  }),
  getters: {
    hasActive(state): boolean {
      return state.items.some((item) => item.status === "pending" || item.status === "uploading")
    },
  },
  actions: {
    enqueue(file: File, folderId?: number) {
      const item = this.push(file, {folderId})

      this.upload(item.id, file, folderId)
    },
    reject(file: File, message: string) {
      this.push(file, {status: "error", message})
    },
    remove(id: string) {
      const index = this.items.findIndex((item) => item.id === id)

      if (index === -1) {
        return
      }

      this.items.splice(index, 1)
    },
    push(file: File, overrides: Partial<UploadItem>): UploadItem {
      const item: UploadItem = {
        id: generateId(),
        name: file.name,
        size: file.size,
        progress: 0,
        indeterminate: false,
        status: "pending",
        message: null,
        ...overrides,
      }

      this.items.push(item)

      return item
    },
    update(id: string, updates: Partial<UploadItem>) {
      const item = this.items.find((item) => item.id === id)

      if (!item) {
        return
      }

      Object.assign(item, updates)
    },
    async upload(id: string, file: File, folderId?: number) {
      const uploader = useFileUpload({
        folderId,
        onProgress: (event) => {
          this.update(id, {
            status: "uploading",
            progress: event.progress,
            indeterminate: event.scanning_for_virus,
            message: event.scanning_for_virus ? "Scanning for viruses..." : null,
          })
        },
      })

      try {
        await uploader(file)

        this.update(id, {
          status: "success",
          message: "File uploaded successfully",
          indeterminate: false,
          progress: 100,
        })

        if (folderId !== undefined) {
          this.completed[folderId] = (this.completed[folderId] ?? 0) + 1
        }
      } catch (error: any) {
        this.update(id, {
          status: "error",
          message: error.message,
          indeterminate: false,
          progress: 100,
        })
      }

      setTimeout(() => this.remove(id), DISMISS_DELAY)
    },
  },
})
